import { isFutureDate } from '@/helpers/validations';
import { captureError } from '@smg-real-estate/browser-rum';

/**
 * maps date formatted as DD.MM.YYYY into date formatted as YYYY-MM-DD
 */
export function mapToHgRetsDateFormat(dotFormattedDate: string): string {
  const dateFields = dotFormattedDate.split('.');
  return `${dateFields[2]}-${dateFields[1]}-${dateFields[0]}`;
}

/**
 * maps date formatted as YYYY-MM-DD into date formatted as DD.MM.YYYY
 */
export function mapToDotFormattedDate(dashFormattedDate: string): string {
  const dateFields = dashFormattedDate.split('-');
  return `${dateFields[2]}.${dateFields[1]}.${dateFields[0]}`;
}

/**
 * maps date formatted as YYYY-MM-DDThh:mm:ss.000Z into date formatted as DD.MM.YYYY
 */
export function mapUtcToDotFormattedDate(utcDate: string): string {
  const dashFormattedDate = utcDate.split('T');
  const dateFields = dashFormattedDate[0].split('-');
  return `${dateFields[2]}.${dateFields[1]}.${dateFields[0]}`;
}

/**
 * maps date formatted as YYYY-MM-DDThh:mm:ss.000Z into date formatted as YYYY-MM-DD
 */
export function mapUtcToHgRetsDateFormat(date: string): string {
  const dotted = mapUtcToDotFormattedDate(date);
  return mapToHgRetsDateFormat(dotted);
}

export function isUtcDateInPast(date: string): boolean {
  return !isHgRetsDatePresentOrFuture(mapUtcToHgRetsDateFormat(date));
}

export function isUtcDateTimeInPast(date: string): boolean {
  const timestamp = Date.parse(date);
  const now = Date.now(); // will be UTC
  return timestamp <= now;
}

export function convertYearMonthToIntlDateString(yearMonth: string, locale: string): string {
  try {
    const [year, month] = yearMonth.split('-');

    if (Number(month) < 1 || Number(month) > 12) {
      throw new Error('Invalid Date');
    }
    const date = new Date(Number(year), Number(month) - 1);

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      throw new Error('Invalid Date');
    }

    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long' };
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch (error) {
    return error.message;
  }
}

export function isHgRetsDatePresentOrFuture(date: string): boolean {
  return isFutureDate(mapToDotFormattedDate(date));
}

export function getCurrentDateInHgRetsFormat(): string {
  const now = new Date();
  return `${now.getFullYear()}-${appendLeadingZeroes(now.getMonth() + 1)}-${appendLeadingZeroes(
    now.getDate(),
  )}`;
}

export function getOneYearFutureDateInHgRetsFormat(): string {
  const now = new Date();
  return `${now.getFullYear() + 1}-${appendLeadingZeroes(now.getMonth() + 1)}-${appendLeadingZeroes(
    now.getDate(),
  )}`;
}

export function getLeadDate(date: number): string {
  const passedDate: Date = new Date(date);

  const formattedDate: string = passedDate.toLocaleDateString(undefined, {
    month: 'long',
    day: '2-digit',
  });

  const formattedTime: string = passedDate.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return `${formattedDate}, ${formattedTime}`;
}

export function getCurrentDateForDisplay(date?: Date | string): string {
  if (!date) {
    return '';
  }

  if (typeof date === 'string' && isNaN(Date.parse(date))) {
    captureError(
      new Error(`dateUtils/getCurrentDateForDisplay given string is not a valid date. [${date}]`),
    );
    return '';
  }

  const passedDate = typeof date === 'string' ? new Date(date) : date;
  const hour = ('00' + passedDate.getHours()).slice(-2);
  const minute = ('00' + passedDate.getMinutes()).slice(-2);
  const dateString = getDateString(passedDate);

  return `${hour}:${minute} ${dateString}`;
}

export function getDateString(date?: Date | string): string {
  if (!date) {
    return '';
  }

  if (typeof date === 'string' && isNaN(Date.parse(date))) {
    captureError(new Error(`dateUtils/getDateString given string is not a valid date. [${date}]`));
    return '';
  }

  const passedDate = typeof date === 'string' ? new Date(date) : date;
  const day = ('00' + passedDate.getDate()).slice(-2);
  const month = ('00' + (passedDate.getMonth() + 1)).slice(-2);

  return `${day}.${month}.${passedDate.getFullYear()}`;
}

export function isToday(date: Date): boolean {
  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

// "2023-05-22T01:00:00.000Z" to "22.05"
export function convertDateToDDMM(isoDate: string): string {
  const date = new Date(isoDate);

  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');

  return `${day}.${month}`;
}

function appendLeadingZeroes(n: number) {
  if (n <= 9) {
    return '0' + n;
  }
  return n;
}

export function convertUTCToLocalFormattedString(
  input: string | undefined,
  isTimeIncluded = true,
): string {
  if (!input) {
    return '';
  }

  const date = new Date(input);

  if (isNaN(date.getTime())) {
    return '';
  }

  const dateFormatter = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const formattedDate = dateFormatter.format(date);

  if (!isTimeIncluded) {
    return formattedDate;
  }

  const timeFormatter = new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const formattedTime = timeFormatter.format(date);

  return `${formattedDate}, ${formattedTime}`;
}

export function calculateDaysUntilTargetDate(date: string): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const targetDate = new Date(date);

  const diff = targetDate.getTime() - now.getTime();

  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

export function calculateDaysBetweenTwoDates(date1: string, date2: string): number | null {
  const firstDate = new Date(date1);
  const secondDate = new Date(date2);

  if (isNaN(firstDate.getTime()) || isNaN(secondDate.getTime())) {
    return null;
  }

  const diff = Math.abs(firstDate.getTime() - secondDate.getTime());

  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

export function convertFormattedStringToDate(input: string): Date | null {
  const dateTimeRegex = /^(\d{2})\.(\d{2})\.(\d{4}),?\s*(\d{2})?:(\d{2})?$/;

  const match = input.match(dateTimeRegex);
  if (!match) {
    return null;
  }

  const [, day, month, year, hours = '00', minutes = '00'] = match;

  return new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes));
}

export function formatLabelDate(isoDate: string | null | undefined, locale: string): string {
  if (!isoDate) {
    return '';
  }
  const parsed = new Date(isoDate);
  if (isNaN(parsed.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(parsed);
}

export function isDateWithinLast24Hours(date?: string): boolean {
  if (!date) {
    return false;
  }

  const now = new Date();
  const timeDifference = now.getTime() - new Date(date).getTime();
  const millisecondsIn24Hours = 24 * 60 * 60 * 1000;

  return timeDifference < millisecondsIn24Hours;
}
